import { useMemo } from "react";
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer,
} from "recharts";

// Brand palette — exact matches with the rest of the site
const ACTUAL = "#FFFFFF";        // realized IEX
const FORECAST = "#CCFF00";      // PowerBESS p50 — brand lime
const BAND_FILL = "rgba(204,255,0,0.14)";
const BAND_STROKE = "rgba(204,255,0,0.4)";
const GRID = "rgba(255,255,255,0.06)";
const AXIS = "rgba(255,255,255,0.35)";

/**
 * ForecastChart — interactive Recharts visualization of one day's
 * PowerBESS p50 forecast vs realized IEX DAM clearing price.
 *
 * @param {Array} data - 96 blocks: [{b, t, actual, p50, p10, p90, err, ape}, …]
 * @param {string} dayLabel - e.g. "Wed 10 Jun"
 * @param {number} height - chart pixel height
 */
export default function ForecastChart({ data, dayLabel, height = 380 }) {
  // Recharts needs p10 as the lower bound and p90-p10 as the "thickness" (stacked area trick)
  const prepared = useMemo(
    () => data.map((b) => ({
      ...b,
      bandLow: b.p10,
      bandSpan:
        b.p10 === null || b.p10 === undefined || b.p90 === null || b.p90 === undefined
          ? null
          : Math.max(0, b.p90 - b.p10),
    })),
    [data]
  );

  // Only label every 8 blocks (= every 2h) on x-axis to keep it readable
  const xTicks = useMemo(
    () => prepared.filter((_, i) => i % 8 === 0).map((d) => d.t),
    [prepared]
  );

  return (
    <div className="fa-chart-r" data-testid="forecast-chart">
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart
          data={prepared}
          margin={{ top: 10, right: 16, bottom: 8, left: 8 }}
        >
          <defs>
            <linearGradient id="bandGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#CCFF00" stopOpacity={0.22} />
              <stop offset="100%" stopColor="#CCFF00" stopOpacity={0.04} />
            </linearGradient>
          </defs>

          <CartesianGrid stroke={GRID} strokeDasharray="2 4" vertical={false} />

          <XAxis
            dataKey="t"
            ticks={xTicks}
            tick={{ fill: AXIS, fontSize: 11, fontFamily: "JetBrains Mono, monospace" }}
            stroke={GRID}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fill: AXIS, fontSize: 11, fontFamily: "JetBrains Mono, monospace" }}
            stroke={GRID}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)}
            domain={[0, 'dataMax + 500']}
            label={{
              value: "₹ / MWh",
              angle: -90, position: "insideLeft", offset: 14,
              style: { fill: AXIS, fontSize: 10, fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.05em" },
            }}
          />

          {/* Price-cap line at ₹10,000 (IEX ceiling) */}
          <ReferenceLine
            y={10000}
            stroke="rgba(255,184,0,0.4)"
            strokeDasharray="4 4"
            label={{ value: "₹10k cap", fill: "#FFB800", fontSize: 9, position: "right" }}
          />

          {/* P10-P90 band: stacked area trick — invisible lower bar + visible span */}
          <Area
            type="monotone" dataKey="bandLow"
            stackId="band" stroke="none" fill="transparent" isAnimationActive={false}
          />
          <Area
            type="monotone" dataKey="bandSpan"
            stackId="band" stroke={BAND_STROKE} strokeWidth={1}
            fill="url(#bandGrad)" isAnimationActive={false}
            name="P10–P90 band"
          />

          {/* Actual IEX DAM (white) */}
          <Line
            type="monotone" dataKey="actual"
            stroke={ACTUAL} strokeWidth={1.8}
            dot={false} isAnimationActive={false}
            name="Actual IEX DAM"
          />

          {/* PowerBESS p50 forecast (lime, dashed) */}
          <Line
            type="monotone" dataKey="p50"
            stroke={FORECAST} strokeWidth={2}
            strokeDasharray="5 4" dot={false} isAnimationActive={false}
            name="PowerBESS p50"
          />

          <Tooltip content={<ChartTooltip dayLabel={dayLabel} />} cursor={{ stroke: "rgba(204,255,0,0.25)", strokeWidth: 1 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// Blocks may legitimately carry null fields: the live T+1 forecast has no
// actual/err/ape yet, and unsettled/partial blocks null out p10/p90. Guard every
// numeric render so a hover never throws (an unguarded `.toLocaleString()` on
// null unmounts the whole page → the "goes blank" crash).
const money = (v) => (v === null || v === undefined ? "—" : `₹ ${Number(v).toLocaleString()}`);

function ChartTooltip({ active, payload, label, dayLabel }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload || {};
  const hasErr = row.err !== null && row.err !== undefined;
  const hasApe = row.ape !== null && row.ape !== undefined;
  const errSign = hasErr && row.err >= 0 ? "+" : "";
  const apeColor = !hasApe ? AXIS : row.ape <= 5 ? "#7CE85F" : row.ape <= 15 ? "#FFB800" : "#FF7A7A";
  return (
    <div className="fa-tip" data-testid="forecast-tip">
      <div className="fa-tip-head">
        <span className="fa-tip-day">{dayLabel}</span>
        <span className="fa-tip-time">{row.t}</span>
        <span className="fa-tip-blk">Blk {row.b}</span>
      </div>
      <div className="fa-tip-rows">
        <div className="fa-tip-row">
          <span className="fa-tip-dot" style={{ background: ACTUAL }} />
          <span className="fa-tip-l">Actual</span>
          <span className="fa-tip-v">{money(row.actual)}</span>
        </div>
        <div className="fa-tip-row">
          <span className="fa-tip-dot" style={{ background: FORECAST }} />
          <span className="fa-tip-l">PB p50</span>
          <span className="fa-tip-v">{money(row.p50)}</span>
        </div>
        <div className="fa-tip-row fa-tip-band">
          <span className="fa-tip-l">P10 – P90</span>
          <span className="fa-tip-v">{money(row.p10)} – {money(row.p90)}</span>
        </div>
        <div className="fa-tip-divider" />
        <div className="fa-tip-row">
          <span className="fa-tip-l">Error</span>
          <span className="fa-tip-v" style={{ color: !hasErr ? AXIS : errSign === "+" ? "#FFB800" : "#7CB8FF" }}>
            {hasErr ? `${errSign}${Number(row.err).toLocaleString()}` : "—"}
          </span>
        </div>
        <div className="fa-tip-row">
          <span className="fa-tip-l">APE</span>
          <span className="fa-tip-v" style={{ color: apeColor }}>{hasApe ? `${Number(row.ape).toFixed(1)}%` : "—"}</span>
        </div>
      </div>
    </div>
  );
}
